import './Balance.css';
import React, { Suspense } from 'react';
import LoadingScreen from './LoadingScreen';
import { createDollarFormatter, createNumberFormatter, createLinkFormatter, dateRangeFilterEditor, minMaxFilterFunction } from './TableUtils.js';
import './TableUtils.css';
import { useSearchParams, defer, Await, useLoaderData } from "react-router-dom";

import 'react-tabulator/lib/styles.css';
import 'react-tabulator/lib/css/tabulator.min.css';
import { ReactTabulator } from 'react-tabulator';

let _rangesCache = null;
const getRanges = async () => {
  if (_rangesCache) return _rangesCache;
  const url = (process.env.NODE_ENV === 'development' ? '/ranges.json' : '/ranges');
  _rangesCache = await fetch(url).then((res) => res.json());
  return _rangesCache;
};

export const loader = async ({ params }) => {
  if (_rangesCache) return { ranges: _rangesCache };
  const ranges = getRanges();
  return defer({ ranges });
};

function searchParamsToFilter(searchParams) {
  const filters = {};
  searchParams.forEach((value, key) => {
    if (key === "account" || key === "symbol") {
      filters[key] = { field: key, value: value, type: "=" };
    } else if (key === "start") {
      const existing = filters["day"] || { field: "day", value: {}, type: "function" };
      existing.value.start = value;
      filters["day"] = existing;
    } else if (key === "end") {
      const existing = filters["day"] || { field: "day", value: {}, type: "function" };
      existing.value.end = value;
      filters["day"] = existing;
    }
  });
  const result = [];
  for (var key in filters) {
    result.push(filters[key]);
  }
  return result;
}

function filterToSearchParams(filters) {
  const result = {};
  filters.forEach((row) => {
    if (row.field === "account" || row.field === "symbol") {
      if (row.value) result[row.field] = row.value;
    } else if (row.field === "day") {
      if (row.value && row.value.start) result["start"] = row.value.start;
      if (row.value && row.value.end) result["end"] = row.value.end;
    }
  });
  return result;
}

function tabulatorUrlParams(params, prefix) {
  var parts = [];
  for (var key in params) {
    var value = params[key];
    var fullKey = prefix ? prefix + "[" + key + "]" : key;
    if (typeof value === "object" && value !== null) {
      parts.push(tabulatorUrlParams(value, fullKey));
    } else {
      parts.push(encodeURIComponent(fullKey) + "=" + encodeURIComponent(value));
    }
  }
  return parts.filter(Boolean).join("&");
}

const _balanceAjaxCache = new Map();

function cachedAjaxRequestFunc(url, config, params) {
  var queryString = tabulatorUrlParams(params);
  var fullUrl = queryString ? url + '?' + queryString : url;
  if (_balanceAjaxCache.has(fullUrl)) {
    return Promise.resolve(_balanceAjaxCache.get(fullUrl));
  }
  return fetch(fullUrl)
    .then(function(res) { return res.json(); })
    .then(function(data) {
      _balanceAjaxCache.set(fullUrl, data);
      return data;
    });
}

const filterClickFn = (field) => (cell) => {
  var hash = window.location.hash.substring(1);
  var qIndex = hash.indexOf('?');
  var currentParams = new URLSearchParams(qIndex >= 0 ? hash.substring(qIndex + 1) : '');
  if (currentParams.get(field) === cell.getValue()) {
    currentParams.delete(field);
  } else {
    currentParams.set(field, cell.getValue());
  }
  var qs = currentParams.toString();
  window.location.hash = '/balance' + (qs ? '?' + qs : '');
};

function clearableListValues(arr) {
  return {"": "-- All --", ...Object.fromEntries(arr.map(v => [v, v]))};
}

function Balance() {
  const loaderData = useLoaderData();
  const [searchParams] = useSearchParams();

  const columns = (ranges) => {
    return [
      { title: "day", field: "day", headerSortStartingDir: "desc", sorter: "date", headerFilter: dateRangeFilterEditor, headerFilterFunc: minMaxFilterFunction, headerFilterLiveFilter: false, minWidth: 320 },
      { title: "account", field: "account", headerFilter: 'list', headerFilterFunc: "=", headerFilterPlaceholder: "-- All --", headerFilterParams: { values: clearableListValues(ranges['accounts']) }, minWidth: 200,
        formatter: createLinkFormatter((cell) => `/balance?account=${cell.getValue()}`, filterClickFn('account')), },
      { title: "symbol", field: "symbol", headerFilter: 'list', headerFilterFunc: "=", headerFilterPlaceholder: "-- All --", headerFilterParams: { values: clearableListValues(ranges['symbols']) }, minWidth: 120,
        formatter: createLinkFormatter((cell) => `/balance?symbol=${cell.getValue()}`, filterClickFn('symbol')), },
      { title: "quantity", field: "quantity", sorter: "number", formatter: createNumberFormatter(2), hozAlign: 'right', minWidth: 120 },
      { title: "value", field: "asset_value", sorter: "number", formatter: createDollarFormatter(2), hozAlign: 'right', minWidth: 120 },
    ];
  };

  const initialFilter = searchParamsToFilter(searchParams);

  const options = {
    ajaxURL: (process.env.NODE_ENV === 'development' ? '/daily_balances.json' : '/daily_balances'),
    ajaxRequestFunc: cachedAjaxRequestFunc,
    ajaxResponse: (url, params, response) => { return response },
    ajaxError: function (error) { console.log('ajaxError', error); },
    pagination: true,
    paginationMode: 'remote',
    paginationSize: 50,
    sortMode: 'remote',
    initialSort: [
      { column: "day", dir: "desc" },
    ],
    filterMode: 'remote',
    initialHeaderFilter: initialFilter,
    layout: "fitDataStretch",
    height: "100%",
  };

  const dataFilteredHandler = (filters, rows) => {
    const newParams = filterToSearchParams(filters);
    const newSearch = new URLSearchParams(newParams).toString();
    const newHash = '#/balance' + (newSearch ? '?' + newSearch : '');
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  };

  const renderTable = (ranges) => {
    return <ReactTabulator
             key={searchParams.toString()}
             options={options}
             columns={columns(ranges)}
             events={{ dataFiltered: dataFilteredHandler }}
           />;
  };

  return (
    <Suspense fallback={<LoadingScreen visible="true" />}>
      <Await resolve={loaderData.ranges}
             errorElement={<div>Error Loading</div>}>
        {(ranges) => (
          <div className="balance">
            {renderTable(ranges)}
          </div>
        )}
      </Await>
    </Suspense>
  );
}

export default Balance;
